<template>
  <div class="guide-quotes-quote">
    <div class="guide-quotes-quote__title">
      <ArticleContentRender :content="title" />
    </div>
    <div class="guide-quotes-quote__text">
      <ArticleContentRender :content="text" />
    </div>

    <div class="guide-quotes-quote__info">
      <img v-if="avatarLink" class="guide-quotes-quote__avatar" :src="avatarLink" loading="lazy" />
      <div class="guide-quotes-quote__info-text">
        <span>
          <AppLink :to="guideProfilePath" target="_blank" class="guide-quotes-quote__guide-name">
            {{ name }}
          </AppLink>
          <span>, {{ fullLocation }}</span>
        </span>

        <div v-if="experienceCount" class="guide-quotes-quote__experiences">
          {{ formattedExperienceCount }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from '@experience/i18n';
import { computed } from 'vue';

import { ArticleContentRender } from '@/features/articleContent';
import type { ArticleContent } from '@/features/articleContent';

import ru from '../i18n/ru.json';

export interface Props {
  id: number;
  name: string;
  avatarLink: string;
  location: string;
  experienceCount: number;
  title: ArticleContent;
  text: ArticleContent;
}

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const fullLocation = computed(() => {
  return props.location ? t('quote.guideIn', { location: props.location }) : '';
});

const props = defineProps<Props>();

const guideProfilePath = computed(() => `/guide/${props.id}`);

const formattedExperienceCount = computed(() => t('quote.experiences', props.experienceCount));
</script>

<style lang="scss" scoped>
.guide-quotes-quote {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.guide-quotes-quote__title,
.guide-quotes-quote__text {
  :deep(> *:first-child) {
    padding-top: 0;
    margin-top: 0;
  }
}

.guide-quotes-quote__title {
  @include base-500-400-extra;

  @include tablet {
    @include base-400-400-semi;
  }
}

.guide-quotes-quote__text {
  @include base-500-500-regular;

  @include tablet {
    @include base-400-400-regular;
  }
}

.guide-quotes-quote__info {
  display: flex;
  gap: var(--alias-space-12);
  padding-top: var(--alias-space-8);
}

.guide-quotes-quote__info-text {
  display: flex;
  flex-direction: column;
  justify-content: center;

  @include base-500-400-regular;

  @include tablet {
    @include base-400-300-regular;
  }
}

.guide-quotes-quote__avatar {
  flex-grow: 0;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  object-fit: cover;
  background-color: var(--alias-color-background-default);
  border-radius: var(--alias-radius-rounded);

  @include tablet {
    width: 32px;
    height: 32px;
  }
}

.guide-quotes-quote__guide-name {
  text-decoration: none;

  @include base-500-400-regular;

  @include tablet {
    @include base-400-300-regular;
  }
}

.guide-quotes-quote__experiences {
  color: var(--alias-color-text-subtle);

  @include base-400-300-regular;

  @include tablet {
    @include base-300-300-regular;
  }
}
</style>
